import { useCallback, useState } from 'react'
import type { Plan, UpgradeRequiredPayload } from '@deepguide-ai/types'
import { UpgradeDialog } from '@/components/plan-restrictions'
import { parseUpgradeRequired, upgradeTargetForPlan } from '@/lib/upgrade-error'

type ShowUpgradeOptions = {
  featureName?: string
  featureDescription?: string
  requiredPlan?: Plan
  source?: string
}

function defaultFeatureName(resource: UpgradeRequiredPayload['resource']) {
  return resource === 'members' ? 'More team members' : 'More frameworks'
}

export function useUpgradeRequiredDialog() {
  const [upgradePayload, setUpgradePayload] =
    useState<UpgradeRequiredPayload | null>(null)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [options, setOptions] = useState<ShowUpgradeOptions>({})

  const showUpgrade = useCallback(
    (payload: UpgradeRequiredPayload, nextOptions: ShowUpgradeOptions = {}) => {
      setUpgradePayload(payload)
      setOptions(nextOptions)
      setUpgradeOpen(true)
      return true
    },
    [],
  )

  const showUpgradeForError = useCallback(
    (error: unknown, nextOptions: ShowUpgradeOptions = {}) => {
      const upgrade = parseUpgradeRequired(error)
      if (!upgrade) return false

      return showUpgrade(upgrade, nextOptions)
    },
    [showUpgrade],
  )

  const upgradeDialog = upgradePayload ? (
    <UpgradeDialog
      open={upgradeOpen}
      onOpenChange={setUpgradeOpen}
      featureName={options.featureName ?? defaultFeatureName(upgradePayload.resource)}
      featureDescription={options.featureDescription ?? upgradePayload.message}
      currentPlan={upgradePayload.plan}
      requiredPlan={options.requiredPlan ?? upgradeTargetForPlan(upgradePayload.plan)}
      source={options.source ?? `${upgradePayload.resource}_limit`}
    />
  ) : null

  return {
    showUpgrade,
    showUpgradeForError,
    upgradeDialog,
  }
}
